import { makeStyles } from '@mui/styles';
import React, { useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '../../app/hooks';
import { TranscriptTypes } from '../../constants/enums';
import { AgentTypes } from '../../constants/event';
import {
  EntitySpan,
  normalizePromotedText,
  PromotedTextRef,
  TextFrameTransmissionMessage,
} from '../../reducers/messagingSlice';
import {
  selectEntityAnnotationsMap,
  selectPromotedTextEvents,
} from '../../selectors/message';
import { selectFeatureFlagSecondaryAsrEnabled } from '../../redux/features/config/config.selector';
import { LOWER_CASE_O, OH } from '../../utils/constants';
import { ISOStringToEpoch } from '../../utils/network';
import { TranscriptMessage } from '../TranscriptMessage';

// secondary_asr: max time gap between a promoted commit and the ASR frame it
// belongs to. A promoted commit lands within ~seconds of its frame, so this
// window keeps a promoted event from binding to an unrelated same-text line far
// away in time (a repeat from another turn, or a line in the buffered pre-HITL
// replay). Generous enough to absorb reprocessing lag, tight enough to separate
// turns/sessions.
const PROMOTED_MATCH_WINDOW_MS = 120_000;

// secondary_asr: resolve which frames in `messageList` are ASR-promoted. Match
// is by text and 1:1 by nearest timestamp: each promoted event claims at most
// one frame and each frame is claimed at most once, so when the same utterance
// repeats only the specific occurrence(s) actually promoted get badged — never
// every line sharing the text.
export const computePromotedFrameIds = (
  messageList: TextFrameTransmissionMessage[],
  promotedEvents: PromotedTextRef[]
): Set<string> => {
  const promotedFrameIds = new Set<string>();
  if (!promotedEvents.length) return promotedFrameIds;

  // Candidate CX (customer, non-TTS) frames grouped by normalized text.
  const framesByText = new Map<string, { id: string; epoch: number }[]>();
  messageList.forEach((message) => {
    if (message.agent_type !== AgentTypes.orderBoard) return;
    if (message.data?.status === 'TTS') return;
    const text = normalizePromotedText(message.data?.payload);
    if (!text) return;
    const bucket = framesByText.get(text) ?? [];
    bucket.push({ id: message.id, epoch: ISOStringToEpoch(message.timestamp) });
    framesByText.set(text, bucket);
  });

  const claimed = new Set<string>();
  [...promotedEvents]
    .sort(
      (a, b) => ISOStringToEpoch(a.timestamp) - ISOStringToEpoch(b.timestamp)
    )
    .forEach((promoted) => {
      const bucket = framesByText.get(promoted.text);
      if (!bucket) return;
      const promotedEpoch = ISOStringToEpoch(promoted.timestamp);
      let bestId = '';
      let bestDiff = Infinity;
      bucket.forEach((frame) => {
        if (claimed.has(frame.id)) return;
        const diff = Math.abs(frame.epoch - promotedEpoch);
        if (diff <= PROMOTED_MATCH_WINDOW_MS && diff < bestDiff) {
          bestDiff = diff;
          bestId = frame.id;
        }
      });
      if (bestId) {
        claimed.add(bestId);
        promotedFrameIds.add(bestId);
      }
    });

  return promotedFrameIds;
};

interface OwnProps {
  messageList: TextFrameTransmissionMessage[];
  // Forwarded to TranscriptMessage; the HITL 3.0 transcript card renders denser
  // messages. Defaults false → original layout.
  compact?: boolean;
}

const useStyles = makeStyles(({}) => ({
  bottomRefClass: {
    paddingBottom: '10px',
  },
}));

export function MessageContainer({ messageList, compact = false }: OwnProps) {
  const messagesBottomRef = useRef<HTMLDivElement>(null);
  const listInnerRef = useRef<HTMLDivElement>(null);
  const { bottomRefClass } = useStyles();
  const entityAnnotationsMap = useAppSelector(selectEntityAnnotationsMap);
  const promotedTextEvents = useAppSelector(selectPromotedTextEvents);
  const secondaryAsrEnabled = useAppSelector(
    selectFeatureFlagSecondaryAsrEnabled
  );
  // secondary_asr: the frames to badge, resolved once per list/events change.
  // Empty when the flag is off, so toggling it is purely presentational.
  const promotedFrameIds = useMemo(
    () =>
      secondaryAsrEnabled
        ? computePromotedFrameIds(messageList, promotedTextEvents)
        : new Set<string>(),
    [secondaryAsrEnabled, messageList, promotedTextEvents]
  );

  const lastMessage =
    messageList.length > 0 ? messageList[messageList.length - 1] : undefined;

  const scrollToBottom = () => {
    if (
      messagesBottomRef &&
      messagesBottomRef.current &&
      listInnerRef &&
      listInnerRef.current
    ) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        messagesBottomRef.current.scrollIntoView?.();
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [lastMessage]);

  const renderMessage = (message: TextFrameTransmissionMessage) => {
    let payload = message.data?.payload;
    const isBlocked = message.data?.blocked_status?.blocked === true;
    const blockedReason = message.data?.blocked_status?.reason;
    const annotations: EntitySpan[] | undefined =
      entityAnnotationsMap?.[message.id];
    if (message.data && message.agent_type == 'ORDERBOARD' && payload) {
      if (message.data.status !== 'TTS') {
        // secondary_asr: badge this line if a promoted ASR commit bound to it
        // (by text + nearest timestamp; see computePromotedFrameIds). Already
        // gated on the flag via the memo above, so this is presentational only.
        const isPromoted = promotedFrameIds.has(message.id);
        return (
          <TranscriptMessage
            key={message.id}
            text={payload}
            secondLangText={message.metadata?.second_lang_text}
            type={TranscriptTypes.CX}
            isBlocked={isBlocked}
            blockedReason={blockedReason}
            entityAnnotations={annotations}
            isPromoted={isPromoted}
            compact={compact}
            timestamp={message.timestamp}
          />
        );
      }

      const regularExpr = /\d+\sOh\s\d+/;
      if (regularExpr.exec(payload)) {
        payload = payload.replace(OH, LOWER_CASE_O); // Display 'Oh' in the total tts visually as 'O'
      }
      return (
        <TranscriptMessage
          key={message.id}
          text={payload}
          secondLangText={message.metadata?.second_lang_text}
          type={TranscriptTypes.AI}
          isBlocked={isBlocked}
          blockedReason={blockedReason}
          compact={compact}
          timestamp={message.timestamp}
        />
      );
    }
    return <></>;
  };

  return (
    <div data-testid="messageContainer" ref={listInnerRef}>
      {messageList.map((message) => renderMessage(message))}
      <div
        data-testid="transcriptBottomRef"
        ref={messagesBottomRef}
        className={bottomRefClass}
      />
    </div>
  );
}
